<script setup>
import { computed } from 'vue'

const props = defineProps({
  activePlans: {
    type: Array,
    default: () => []
  },
  hasBasicMethods: {
    type: Boolean,
    default: false
  },
  hasSilverMethods: {
    type: Boolean,
    default: false
  },
  hasGoldMethods: {
    type: Boolean,
    default: false
  }
})

const hasMultiplePlans = computed(() => props.activePlans.length > 1)

const planTier = computed(() => {
  if (props.hasGoldMethods) return 'gold'
  if (props.hasSilverMethods) return 'silver'
  if (props.hasBasicMethods) return 'basic'
  return null
})

const planTierName = computed(() => {
  switch (planTier.value) {
    case 'gold': return 'Gold Plan'
    case 'silver': return 'Silver Plan'
    case 'basic': return 'Basic Plan'
    default: return 'No Plan'
  }
})
</script>

<template>
  <section id="pricing-info" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
    <div id="multi-module-info">
      <div class="flex items-start space-x-4 mb-6">
        <div class="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <span class="material-symbols-outlined text-white text-xl">layers</span>
        </div>
        <div>
          <h4 class="text-lg font-bold text-gray-900 mb-2">Your Pricing Plan{{ hasMultiplePlans ? 's' : '' }}</h4>
          <p v-if="hasMultiplePlans" class="text-sm text-gray-600">
            You've selected methods across <strong>{{ activePlans.length }} tiers</strong>. You'll set pricing for each tier in the next step.
          </p>
          <p v-else-if="planTier" class="text-sm text-gray-600">
            Based on your selection, you'll be on the <strong>{{ planTierName }}</strong>
          </p>
          <p v-else class="text-sm text-gray-600">Select your teaching methods to see your pricing plan</p>
        </div>
      </div>
      <div class="space-y-3">
        <div
          class="flex items-center justify-between p-3 rounded-lg transition-all"
          :class="hasBasicMethods ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-50'"
        >
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined" :class="hasBasicMethods ? 'text-blue-600' : 'text-gray-600'">star</span>
            <span class="text-sm font-semibold" :class="hasBasicMethods ? 'text-blue-900' : 'text-gray-900'">Basic Plan</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-500">Study Materials & Videos</span>
            <span v-if="hasBasicMethods" class="material-symbols-outlined text-blue-600">check_circle</span>
          </div>
        </div>
        <div
          class="flex items-center justify-between p-3 rounded-lg transition-all"
          :class="hasSilverMethods ? 'bg-purple-100 border-2 border-purple-500' : 'bg-gray-50'"
        >
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined" :class="hasSilverMethods ? 'text-purple-600' : 'text-gray-600'">groups</span>
            <span class="text-sm font-semibold" :class="hasSilverMethods ? 'text-purple-900' : 'text-gray-900'">Silver Plan</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-500">Group Tutoring</span>
            <span v-if="hasSilverMethods" class="material-symbols-outlined text-purple-600">check_circle</span>
          </div>
        </div>
        <div
          class="flex items-center justify-between p-3 rounded-lg transition-all"
          :class="hasGoldMethods ? 'bg-amber-100 border-2 border-amber-500' : 'bg-gray-50'"
        >
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined" :class="hasGoldMethods ? 'text-amber-600' : 'text-gray-600'">diamond</span>
            <span class="text-sm font-semibold" :class="hasGoldMethods ? 'text-amber-900' : 'text-gray-900'">Gold Plan</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-500">1:1 Tutoring</span>
            <span v-if="hasGoldMethods" class="material-symbols-outlined text-amber-600">check_circle</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
